import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { t } from '../utils/translations';
import { fetchConfig, updateConfig, ConfigEntry } from '../api';

interface ConfigData {
  grayUrl?: string;
  prodUrl?: string;
}

const ConfigManagement: React.FC = () => {
  const [config, setConfig] = useState<ConfigData>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { language } = useApp();

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    if (!url) return true; // Empty URL is valid
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Convert array format to object format for easier UI handling
  const convertArrayToConfig = (configArray: ConfigEntry[]): ConfigData => {
    const result: ConfigData = {};
    
    configArray.forEach(entry => {
      if (entry.platform === 'desktop') {
        if (entry.env === 'gray') {
          result.grayUrl = entry.url;
        } else if (entry.env === 'prod') {
          result.prodUrl = entry.url;
        }
      }
    });
    
    return result;
  };

  // Convert object format back to array format for API
  const convertConfigToArray = (config: ConfigData): ConfigEntry[] => {
    const result: ConfigEntry[] = [];
    
    if (config.grayUrl !== undefined) {
      result.push({
        env: 'gray',
        platform: 'desktop',
        url: config.grayUrl
      });
    }
    
    if (config.prodUrl !== undefined) {
      result.push({
        env: 'prod',
        platform: 'desktop',
        url: config.prodUrl
      });
    }
    
    return result;
  };

  // Load configuration from API
  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const configArray = await fetchConfig();
      console.log('Loaded config array:', configArray);
      
      const configData = convertArrayToConfig(configArray);
      setConfig(configData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Failed to load config:', errorMessage);
      setError(t('loadConfigFailed', language));
    } finally {
      setLoading(false);
    }
  };

  // Save configuration to API
  const saveConfig = async (field: string, value: string) => {
    // Validate URL
    if (!isValidUrl(value)) {
      setError(t('invalidUrlFormat', language));
      return false;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Update the config object
      const updatedConfig = { ...config, [field]: value };
      
      // Convert to array format for API
      const configArray = convertConfigToArray(updatedConfig);

      const result = await updateConfig(configArray);
      console.log('Config saved successfully:', result);
      
      // Important: Reload data from backend to ensure consistency
      await loadConfig();
      
      setSuccessMessage(t('configSaved', language));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Failed to save config:', errorMessage);
      setError(t('saveConfigFailed', language));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Handle inline editing
  const handleEdit = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const handleSave = async () => {
    if (editingField) {
      const success = await saveConfig(editingField, editValue);
      if (success) {
        setEditingField(null);
        setEditValue('');
      }
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Load configuration on component mount
  useEffect(() => {
    loadConfig();
  }, []);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {t('urlConfiguration', language)}
        </h2>
        <button
          onClick={loadConfig}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-150"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {loading ? t('loading', language) : t('refresh', language)}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6" role="alert">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg mb-6" role="alert">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Configuration Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('environment', language)}
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('platform', language)}
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('url', language)}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700/50 divide-y divide-gray-200 dark:divide-gray-600">
              {/* Gray Environment */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                    Gray
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  Desktop
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <div className="flex-1 min-w-0">
                      {editingField === 'grayUrl' ? (
                        <div className="relative">
                          <input
                            type="url"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="w-full h-10 pl-4 pr-20 py-2 text-sm font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                            placeholder="https://example.com/gray"
                            disabled={saving}
                            autoFocus
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              className="inline-flex items-center justify-center w-7 h-7 text-white bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              {saving ? (
                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={handleCancel}
                              disabled={saving}
                              className="inline-flex items-center justify-center w-7 h-7 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="relative group">
                          <div className="w-full h-10 pl-4 pr-12 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center transition-all duration-200 group-hover:border-gray-300 dark:group-hover:border-gray-500 group-hover:shadow-sm min-w-0" style={{ minWidth: '300px' }}>
                            <code className="text-sm font-mono text-gray-700 dark:text-gray-300 truncate min-w-0 w-full">
                              {loading ? (
                                <span className="text-gray-400 dark:text-gray-500 italic">
                                  {t('loading', language)}...
                                </span>
                              ) : config.grayUrl ? (
                                config.grayUrl
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 italic">
                                  {t('notConfigured', language)}
                                </span>
                              )}
                            </code>
                          </div>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <button
                              onClick={() => handleEdit('grayUrl', config.grayUrl || '')}
                              className={`inline-flex items-center justify-center w-7 h-7 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 rounded-md transition-all duration-200 shadow-sm hover:shadow-md ${
                                config.grayUrl ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {config.grayUrl ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                )}
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
              
              {/* Prod Environment */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    Prod
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  Desktop
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  <div className="flex items-center">
                    <div className="flex-1 min-w-0">
                      {editingField === 'prodUrl' ? (
                        <div className="relative">
                          <input
                            type="url"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="w-full h-10 pl-4 pr-20 py-2 text-sm font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                            placeholder="https://example.com/prod"
                            disabled={saving}
                            autoFocus
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                            <button
                              onClick={handleSave}
                              disabled={saving}
                              className="inline-flex items-center justify-center w-7 h-7 text-white bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              {saving ? (
                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={handleCancel}
                              disabled={saving}
                              className="inline-flex items-center justify-center w-7 h-7 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="relative group">
                          <div className="w-full h-10 pl-4 pr-12 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center transition-all duration-200 group-hover:border-gray-300 dark:group-hover:border-gray-500 group-hover:shadow-sm min-w-0" style={{ minWidth: '300px' }}>
                            <code className="text-sm font-mono text-gray-700 dark:text-gray-300 truncate min-w-0 w-full">
                              {loading ? (
                                <span className="text-gray-400 dark:text-gray-500 italic">
                                  {t('loading', language)}...
                                </span>
                              ) : config.prodUrl ? (
                                config.prodUrl
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 italic">
                                  {t('notConfigured', language)}
                                </span>
                              )}
                            </code>
                          </div>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <button
                              onClick={() => handleEdit('prodUrl', config.prodUrl || '')}
                              className={`inline-flex items-center justify-center w-7 h-7 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 rounded-md transition-all duration-200 shadow-sm hover:shadow-md ${
                                config.prodUrl ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {config.prodUrl ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                )}
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ConfigManagement; 